import {
  buildTaskImportPlan,
  buildXlsxProjectReplacePlan,
  type TaskImportMapping,
  type TaskImportPlan,
  type XlsxProjectReplacePlan,
  type XlsxRawValue,
  type XlsxSheetPreview,
  type XlsxWorkbookPreview,
} from '../../integration/xlsx-workbook.js';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

type ImportMode = 'replace' | 'append';
type MappingField = keyof TaskImportMapping;
type DraftMapping = { -readonly [Key in keyof TaskImportMapping]: TaskImportMapping[Key] };

const FOCUSABLE_SELECTOR = 'button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
const MAPPING_FIELDS: readonly { readonly key: MappingField; readonly label: string; readonly required?: boolean; readonly candidates: readonly string[] }[] = [
  { key: 'name', label: '任務名稱', required: true, candidates: ['Name', 'Task Name', '名稱'] },
  { key: 'outlineLevel', label: '大綱層級', candidates: ['Outline Level', 'OutlineLevel', '大綱層級'] },
  { key: 'start', label: '開始日期', candidates: ['Start', '開始'] },
  { key: 'finish', label: '完成日期', candidates: ['Finish', '完成'] },
  { key: 'durationMinutes', label: '工期（分鐘）', candidates: ['Duration Minutes', 'DurationMinutes'] },
  { key: 'workMinutes', label: '工時（分鐘）', candidates: ['Work Minutes', 'WorkMinutes'] },
  { key: 'percentComplete', label: '完成百分比', candidates: ['Percent Complete', 'PercentComplete'] },
  { key: 'fixedCost', label: '固定成本', candidates: ['Fixed Cost', 'FixedCost'] },
];

export interface XlsxImportDialogProps {
  readonly isOpen: boolean;
  readonly workbook: XlsxWorkbookPreview;
  readonly onApply: (input: { readonly sheetName: string; readonly mapping: TaskImportMapping; readonly mode: ImportMode }) => void;
  readonly onReplaceProject: (plan: XlsxProjectReplacePlan) => void;
  readonly onCancel: () => void;
}

function suggestedMapping(sheet: XlsxSheetPreview | undefined): DraftMapping {
  if (sheet === undefined) return { name: '' };

  const mapping: DraftMapping = { name: '' };
  for (const field of MAPPING_FIELDS) {
    const header = sheet.headers.find((candidate) => field.candidates.some((name) => name.toLocaleLowerCase() === candidate.toLocaleLowerCase()));
    if (header !== undefined) mapping[field.key] = header;
  }
  return mapping;
}

function valueText(value: XlsxRawValue): string {
  return value === null ? '' : String(value);
}

function ProjectReplacementOffer({ plan, onConfirm, buttonRef }: { readonly plan: XlsxProjectReplacePlan; readonly onConfirm: () => void; readonly buttonRef: React.RefObject<HTMLButtonElement> }): React.JSX.Element {
  const isValid = plan.diagnostics.length === 0;
  return <section aria-label="完整專案取代" className="rounded border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/50">
    {isValid ? <>
      <p className="font-medium">完整專案取代可用</p>
      <p className="mt-1 text-slate-600 dark:text-slate-300">任務 {plan.tasks.length}、資源 {plan.resources.length}、指派 {plan.assignments.length}、相依性 {plan.dependencies.length}</p>
      <button ref={buttonRef} type="button" onClick={onConfirm} className="mt-2 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white">完整專案取代</button>
    </> : <>
      <p className="font-medium">完整專案取代不可用</p>
      <p className="mt-1 text-slate-600 dark:text-slate-300">需要精確的 Tasks、Resources、Assignments、Dependencies 四工作表。</p>
      <ul className="mt-1 list-disc pl-5 text-xs text-slate-500 dark:text-slate-400">
        {plan.diagnostics.slice(0, 3).map((diagnostic, index) => <li key={`${diagnostic.sheet}-${diagnostic.sourceRow}-${index}`}>{diagnostic.sheet}：{diagnostic.message}</li>)}
      </ul>
    </>}
  </section>;
}

function MappingControls({ workbook, sheet, mapping, mode, mappingRef, onSheetChange, onFieldChange, onModeChange }: {
  readonly workbook: XlsxWorkbookPreview;
  readonly sheet: XlsxSheetPreview;
  readonly mapping: DraftMapping;
  readonly mode: ImportMode;
  readonly mappingRef: React.RefObject<HTMLSelectElement>;
  readonly onSheetChange: (sheetName: string) => void;
  readonly onFieldChange: (field: MappingField, header: string) => void;
  readonly onModeChange: (mode: ImportMode) => void;
}): React.JSX.Element {
  return <section aria-label="工作表與匯入方式" className="space-y-4">
    <label className="block text-sm font-medium" htmlFor="xlsx-import-sheet">
      工作表
      <select id="xlsx-import-sheet" value={sheet.name} onChange={(event) => onSheetChange(event.target.value)} className="mt-1 block w-full rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600">
        {workbook.sheets.map((candidate) => <option key={candidate.name} value={candidate.name}>{candidate.name}</option>)}
      </select>
    </label>
    <fieldset>
      <legend className="text-sm font-medium">匯入方式</legend>
      <label className="mt-1 flex items-center gap-2 text-sm"><input type="radio" name="xlsx-import-mode" checked={mode === 'replace'} onChange={() => onModeChange('replace')} />取代目前任務</label>
      <label className="mt-1 flex items-center gap-2 text-sm"><input type="radio" name="xlsx-import-mode" checked={mode === 'append'} onChange={() => onModeChange('append')} />附加至目前任務</label>
    </fieldset>
    <section aria-labelledby="xlsx-mapping-title">
      <h3 id="xlsx-mapping-title" className="text-sm font-medium">欄位對應</h3>
      <div className="mt-2 space-y-2">
        {MAPPING_FIELDS.map((field, index) => <label key={field.key} className="grid grid-cols-[9rem_minmax(0,1fr)] items-center gap-2 text-sm">
          <span>{field.label}{field.required ? '（必填）' : ''}</span>
          <select ref={index === 0 ? mappingRef : undefined} aria-label={`${field.label} 欄位`} value={mapping[field.key] ?? ''} onChange={(event) => onFieldChange(field.key, event.target.value)} className="min-w-0 rounded border border-slate-300 bg-transparent px-2 py-1.5 dark:border-slate-600">
            <option value="">{field.required ? '請選擇欄位' : '不匯入'}</option>
            {sheet.headers.map((header) => <option key={header} value={header} disabled={mapping[field.key] !== header && Object.entries(mapping).some(([key, value]) => key !== field.key && value === header)}>{header}</option>)}
          </select>
        </label>)}
      </div>
    </section>
  </section>;
}

function WorkbookPreview({ sheet }: { readonly sheet: XlsxSheetPreview }): React.JSX.Element {
  return <section aria-labelledby="xlsx-preview-title" className="min-w-0">
    <h3 id="xlsx-preview-title" className="text-sm font-medium">資料預覽</h3>
    <div className="mt-2 max-h-72 overflow-auto rounded border border-slate-200 dark:border-slate-700">
      <table className="min-w-full text-left text-sm">
        <caption className="sr-only">{sheet.name} 前 20 列資料預覽</caption>
        <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800"><tr>{sheet.headers.map((header, index) => <th key={`${header}-${index}`} scope="col" className="whitespace-nowrap px-2 py-2 font-medium">{header}</th>)}</tr></thead>
        <tbody>{sheet.rows.slice(0, 20).map((row, rowIndex) => <tr key={rowIndex} className="border-t border-slate-100 dark:border-slate-800">{sheet.headers.map((_, columnIndex) => <td key={columnIndex} className="max-w-48 truncate px-2 py-1.5">{valueText(row[columnIndex] ?? null)}</td>)}</tr>)}</tbody>
      </table>
    </div>
    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">顯示前 {Math.min(20, sheet.rows.length)} 列，共 {sheet.rows.length} 列資料。</p>
  </section>;
}

function ValidationMessage({ mapping, plan }: { readonly mapping: DraftMapping; readonly plan: TaskImportPlan | null }): React.JSX.Element {
  if (mapping.name === '') return <p role="alert" className="mt-4 text-sm text-red-600">請對應必填的任務名稱欄位。</p>;
  if (plan !== null && plan.diagnostics.length > 0) return <section aria-label="匯入驗證問題" role="alert" className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
    <h3 className="font-medium">無法匯入：發現 {plan.diagnostics.length} 個問題</h3>
    <ul className="mt-1 list-disc pl-5">{plan.diagnostics.slice(0, 20).map((diagnostic, index) => <li key={`${diagnostic.sourceRow}-${diagnostic.field}-${index}`}>第 {diagnostic.sourceRow} 列，{diagnostic.field}：{diagnostic.message}</li>)}</ul>
  </section>;
  if (plan !== null && plan.rows.length === 0) return <p role="status" className="mt-4 text-sm text-slate-600 dark:text-slate-300">此工作表沒有可匯入的資料列。</p>;
  return <p className="mt-4 text-sm text-emerald-700 dark:text-emerald-300">驗證完成：可匯入 {plan?.rows.length ?? 0} 筆任務。</p>;
}

function ConfirmationDialog({ isProjectReplacement, dialogRef, cancelRef, onCancel, onConfirm, onKeyDown }: {
  readonly isProjectReplacement: boolean;
  readonly dialogRef: React.RefObject<HTMLDivElement>;
  readonly cancelRef: React.RefObject<HTMLButtonElement>;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}): React.JSX.Element {
  const description = isProjectReplacement
    ? '完整專案取代會移除目前的任務、資源、指派與相依性；所有基準線與預算配置也會移除。確定繼續嗎？'
    : '取代會移除目前所有任務，再套用已驗證的 XLSX 工作表。確定繼續嗎？';

  return <div className="absolute inset-0 z-10 flex items-end bg-slate-950/20">
    <div ref={dialogRef} role="alertdialog" aria-modal="true" aria-label={isProjectReplacement ? '確認完整專案取代' : '確認取代目前任務'} onKeyDown={onKeyDown} className="m-5 w-full rounded border border-red-200 bg-white p-4 shadow-lg dark:border-red-900 dark:bg-slate-900">
      <p className="text-sm">{description}</p>
      <div className="mt-3 flex justify-end gap-2">
        <button ref={cancelRef} type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600">返回</button>
        <button type="button" onClick={onConfirm} className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white">{isProjectReplacement ? '確認完整取代' : '確認取代'}</button>
      </div>
    </div>
  </div>;
}

/** Pure workbook-preview mapping dialog. Parsing and project mutation remain with its caller. */
export function XlsxImportDialog({ isOpen, workbook, onApply, onReplaceProject, onCancel }: XlsxImportDialogProps): React.JSX.Element | null {
  const [sheetName, setSheetName] = useState('');
  const [mapping, setMapping] = useState<DraftMapping>({ name: '' });
  const [mode, setMode] = useState<ImportMode>('replace');
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmProjectReplace, setConfirmProjectReplace] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mappingRef = useRef<HTMLSelectElement>(null);
  const confirmationRef = useRef<HTMLDivElement>(null);
  const confirmationCancelRef = useRef<HTMLButtonElement>(null);
  const replaceButtonRef = useRef<HTMLButtonElement>(null);
  const projectReplaceButtonRef = useRef<HTMLButtonElement>(null);
  const restoreConfirmationFocusRef = useRef(false);
  const confirmationOriginRef = useRef<HTMLElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const sheet = workbook.sheets.find((candidate) => candidate.name === sheetName) ?? workbook.sheets[0];
  const plan = useMemo(() => sheet === undefined || mapping.name === '' ? null : buildTaskImportPlan(sheet, mapping), [mapping, sheet]);
  const projectReplacePlan = useMemo(() => buildXlsxProjectReplacePlan(workbook), [workbook]);
  const projectReplaceValid = projectReplacePlan.diagnostics.length === 0;
  const hasConfirmation = confirmReplace || confirmProjectReplace;
  const canApplyTaskImport = sheet !== undefined && mapping.name !== '' && plan !== null && plan.diagnostics.length === 0 && plan.rows.length > 0;

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const initialSheet = workbook.sheets[0];
      setSheetName(initialSheet?.name ?? '');
      setMapping(suggestedMapping(initialSheet));
      setMode('replace');
      setConfirmReplace(false);
      setConfirmProjectReplace(false);
      returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      mappingRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, workbook]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (hasConfirmation) confirmationCancelRef.current?.focus();
    else if (restoreConfirmationFocusRef.current) {
      confirmationOriginRef.current?.focus();
      confirmationOriginRef.current = null;
      restoreConfirmationFocusRef.current = false;
    }
  }, [hasConfirmation]);

  if (!isOpen) return null;

  const selectSheet = (nextName: string): void => {
    const next = workbook.sheets.find((candidate) => candidate.name === nextName);
    setSheetName(nextName);
    setMapping(suggestedMapping(next));
  };
  const setField = (field: MappingField, header: string): void => {
    setMapping((current) => {
      const next: DraftMapping = { ...current };
      if (header === '') delete next[field];
      else {
        (Object.keys(next) as MappingField[]).forEach((key) => {
          if (key !== field && next[key] === header) delete next[key];
        });
        next[field] = header;
      }
      return { ...next, name: next.name ?? '' };
    });
  };
  const applyTaskImport = (): void => {
    if (!canApplyTaskImport || sheet === undefined) return;
    if (mode === 'replace') {
      confirmationOriginRef.current = replaceButtonRef.current;
      setConfirmReplace(true);
      return;
    }
    onApply({ sheetName: sheet.name, mapping, mode });
  };
  const confirmTaskReplacement = (): void => {
    if (sheet === undefined) return;
    setConfirmReplace(false);
    onApply({ sheetName: sheet.name, mapping, mode: 'replace' });
  };
  const openProjectReplaceConfirmation = (): void => {
    if (!projectReplaceValid) return;
    confirmationOriginRef.current = projectReplaceButtonRef.current;
    setConfirmProjectReplace(true);
  };
  const confirmProjectReplacement = (): void => {
    if (!projectReplaceValid) return;
    setConfirmProjectReplace(false);
    onReplaceProject(projectReplacePlan);
  };
  const cancelConfirmation = (): void => {
    restoreConfirmationFocusRef.current = true;
    setConfirmReplace(false);
    setConfirmProjectReplace(false);
  };
  const trapFocus = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (hasConfirmation) cancelConfirmation();
      else onCancel();
      return;
    }
    if (event.key !== 'Tab') return;

    const root = hasConfirmation ? confirmationRef.current : dialogRef.current;
    const focusable = root?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="presentation">
    <div ref={dialogRef} role="dialog" aria-modal="true" aria-hidden={hasConfirmation || undefined} aria-labelledby={titleId} aria-describedby={descriptionId} onKeyDown={trapFocus} className={`flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-slate-900${hasConfirmation ? ' pointer-events-none' : ''}`}>
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-700">
        <div>
          <h2 id={titleId} className="text-lg font-semibold">匯入 XLSX 任務</h2>
          <p id={descriptionId} className="mt-1 text-sm text-slate-600 dark:text-slate-300">選擇工作表和欄位後，會先嚴格驗證所有資料，才交由專案匯入流程套用。</p>
        </div>
        <button type="button" aria-label="關閉 XLSX 匯入" onClick={onCancel} className="rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800">×</button>
      </header>
      {sheet === undefined ? <p className="m-5 rounded border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">此活頁簿沒有可匯入的工作表。</p> : <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <ProjectReplacementOffer plan={projectReplacePlan} onConfirm={openProjectReplaceConfirmation} buttonRef={projectReplaceButtonRef} />
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <MappingControls workbook={workbook} sheet={sheet} mapping={mapping} mode={mode} mappingRef={mappingRef} onSheetChange={selectSheet} onFieldChange={setField} onModeChange={setMode} />
          <WorkbookPreview sheet={sheet} />
        </div>
        <ValidationMessage mapping={mapping} plan={plan} />
      </div>}
      <footer className="flex justify-end gap-2 border-t border-slate-200 p-5 dark:border-slate-700">
        <button type="button" onClick={onCancel} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium dark:border-slate-600">取消</button>
        <button ref={replaceButtonRef} type="button" disabled={!canApplyTaskImport} onClick={applyTaskImport} className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50">{mode === 'replace' ? '驗證並取代' : '匯入並附加'}</button>
      </footer>
    </div>
    {hasConfirmation ? <ConfirmationDialog isProjectReplacement={confirmProjectReplace} dialogRef={confirmationRef} cancelRef={confirmationCancelRef} onCancel={cancelConfirmation} onConfirm={confirmProjectReplace ? confirmProjectReplacement : confirmTaskReplacement} onKeyDown={trapFocus} /> : null}
  </div>;
}

export default XlsxImportDialog;
